import { useState, useEffect } from "react";
import { X, User, CheckCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../data/constants";
import { updateUserDetails } from "../services/usersService";

/**
 * Modal to display and edit authenticated user profile details.
 */
export default function ProfileDetailsModal({ open, onClose }) {
  const { user, login } = useAuth();
  
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("admin");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (open && user) {
      const stored = localStorage.getItem("user_details");
      let storedPhone = "";
      let storedName = "";
      let storedRole = "";
      if (stored) {
        try {
          const u = JSON.parse(stored);
          storedPhone = u.phoneNumber || u.contactNumber || "";
          storedName = u.display_name || "";
          storedRole = u.role || "";
        } catch (e) {
          console.error("Error parsing stored user details:", e);
        }
      }
      setName(storedName || user.name || "");
      setRole(storedRole || user.role || "admin");
      setPhoneNumber(storedPhone || "+91 98765-43210");
      setEditMode(false);
      setError("");
      setSuccess("");
    }
  }, [open, user]);

  if (!open || !user) return null;

  const handleEdit = (e) => {
    e.preventDefault();
    setEditMode(true);
    setError("");
    setSuccess("");
  };

  const handleCancel = (e) => {
    e.preventDefault();
    // Revert inputs
    const stored = localStorage.getItem("user_details");
    let storedPhone = "";
    let storedName = "";
    let storedRole = "";
    if (stored) {
      try {
        const u = JSON.parse(stored);
        storedPhone = u.phoneNumber || u.contactNumber || "";
        storedName = u.display_name || "";
        storedRole = u.role || "";
      } catch (err) {}
    }
    setName(storedName || user.name || "");
    setRole(storedRole || user.role || "admin");
    setPhoneNumber(storedPhone || "+91 98765-43210");
    setEditMode(false);
    setError("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!phoneNumber.trim()) {
      setError("Phone number is required.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("tokens");
      const res = await fetch("https://distinguished-robby-aagati-15819a4f.koyeb.app/api/v1/admin/auth/profile", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ name, mobile_number: phoneNumber })
      });
      const resData = await res.json();
      
      if (!res.ok || !resData.success) {
        throw new Error(resData.message || "Failed to update profile details.");
      }

      // Sync Context and LocalStorage
      const stored = localStorage.getItem("user_details");
      let uDetails = {};
      if (stored) {
        try {
          uDetails = JSON.parse(stored);
        } catch (e) {}
      }

      const updatedDetails = {
        ...uDetails,
        display_name: name,
        role: role,
        phoneNumber: phoneNumber,
      };

      localStorage.setItem("user_details", JSON.stringify(updatedDetails));

      // Sync with Context
      login({
        ...user,
        name: name,
        role: role,
        avatar: name.split(" ").map((n) => n[0]).join(""),
      });

      setSuccess("Profile details updated successfully.");
      setTimeout(() => {
        setEditMode(false);
        setSuccess("");
      }, 1200);
    } catch (err) {
      setError(err.message || "Failed to update profile details.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-[#00000059] rounded-[8px] h-[50px] px-4 outline-none text-gray-800 text-[14px] placeholder:text-gray-400 disabled:bg-gray-100/50 disabled:cursor-not-allowed disabled:text-black disabled:opacity-100";
  const lblCls = "block text-[13px] font-semibold text-[#00000099] mb-1.5";

  return (
    <div
      onClick={loading ? undefined : onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0000001A] backdrop-blur-[14px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] mx-4 p-7 relative bg-[#E7F0F1] border border-[#00000033] backdrop-blur-[20px] shadow-[0px_0px_32px_0px_#3AA1A333] rounded-[20px]"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0D6170]/10 flex items-center justify-center text-[#0D6170]">
              <User size={20} />
            </div>
            <div>
              <h2 className="text-[22px] font-bold text-gray-900 m-0 leading-tight">
                Profile Details
              </h2>
              <p className="text-[13px] text-gray-400 mt-1">
                {editMode ? "Edit your user details below." : "View your account registration details."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer disabled:opacity-50 text-gray-500 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Feedback alerts */}
        {error && (
          <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600 font-medium">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-5 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-600 font-medium flex items-center gap-2">
            <CheckCircle size={16} />
            {success}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Full Name */}
            <div>
              <label className={lblCls}>Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading || !editMode}
                placeholder="Your Full Name"
                className={inputCls}
              />
            </div>

            {/* Email ID (Non-editable) */}
            <div>
              <label className={lblCls}>Email ID (Read Only)</label>
              <input
                type="email"
                value={user.email}
                disabled={true}
                placeholder="email@zytama.com"
                className={inputCls}
              />
            </div>

            {/* Phone Number */}
            <div>
              <label className={lblCls}>Phone Number</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={loading || !editMode}
                placeholder="+91 XXXXX-XXXXX"
                className={inputCls}
              />
            </div>

            <div>
              <label className={lblCls}>Role (Read Only)</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={true}
                className={`${inputCls} appearance-none`}
              >
                <option value="admin">Admin</option>
                <option value="reviewer">Reviewer</option>
                <option value="agent">Agent</option>
              </select>
            </div>
          </div>

          {/* Footer controls */}
          <div className="flex items-center justify-end border-t border-gray-200/50 pt-5 mt-6">
            <div className="flex items-center gap-3">
              {editMode ? (
                <>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={loading}
                    className="h-[40px] px-5 bg-white border border-[#0B617233] rounded-[8px] text-[13px] font-semibold text-[#5E5E5E] cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !name.trim() || !phoneNumber.trim()}
                    className="h-[40px] px-5 bg-[#0B6172] border border-[#0B617233] rounded-[8px] text-[13px] font-semibold text-white cursor-pointer hover:bg-[#094f5e] transition-colors disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    className="h-[40px] px-5 bg-white border border-[#0B617233] rounded-[8px] text-[13px] font-semibold text-[#5E5E5E] cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="h-[40px] px-5 bg-[#0B6172] border border-[#0B617233] rounded-[8px] text-[13px] font-semibold text-white cursor-pointer hover:bg-[#094f5e] transition-colors"
                  >
                    Edit Profile
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
